import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../apiClient';
import { setStoredGalleryId } from '../galleryStorage';
import { useUploadQueue } from '../hooks/useUploadQueue';
import { applyPublicConfigToStorageAndUi } from '../utils/galleryPublicConfigSync';

export const GalleryContext = createContext(null);

// GalleryProvider is rendered under UploadQueueProvider at the app root because
// gallery changes purge pending offline uploads for the previous gallery.

/**
 * @typedef {Object} PublicGalleryLogo
 * @property {string} [logo]
 * @property {string} [text]
 */

/**
 * @typedef {Object} PublicConfig
 * @property {string} galleryId
 * @property {string} name
 * @property {string} status
 * @property {string|null} goLiveAt
 * @property {boolean} previewAuthorized
 * @property {object|null} theme
 * @property {object|null} tabs
 * @property {PublicGalleryLogo|null} logo
 * @property {string|null} deployedAt
 */

function normalizeLogoForSpinner(publicConfig) {
  if (!publicConfig?.logo || typeof publicConfig.logo !== 'object') {
    return '/placeholder-logo.svg';
  }
  const url = publicConfig.logo.logo;
  return typeof url === 'string' && url.length > 0 ? url : '/placeholder-logo.svg';
}

/**
 * @param {{ children: React.ReactNode, galleryId: string, previewToken?: string }} props
 */
export function GalleryProvider({ children, galleryId, previewToken = '' }) {
  const routeGalleryId = typeof galleryId === 'string' ? galleryId.trim() : '';
  const bootstrapPreviewToken =
    typeof previewToken === 'string' ? previewToken.trim() : '';
  const [publicConfig, setPublicConfig] = useState(
    /** @type {PublicConfig|null} */ (null)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {'not-found'|'network'|null} */ (null));

  const currentGalleryIdentityRef = useRef({ routeId: null, canonicalId: null });
  const configRouteGalleryIdRef = useRef(null);
  const pendingPurgeGalleryIdsRef = useRef(new Set());
  const configRequestRef = useRef(0);

  const flushPendingPurges = useCallback((activeGalleryIds = []) => {
    const sw = navigator.serviceWorker?.controller;
    activeGalleryIds.filter(Boolean).forEach((activeGalleryId) => {
      pendingPurgeGalleryIdsRef.current.delete(activeGalleryId);
    });
    if (sw && pendingPurgeGalleryIdsRef.current.size > 0) {
      pendingPurgeGalleryIdsRef.current.forEach((pendingGalleryId) => {
        sw.postMessage({
          type: 'PURGE_GALLERY',
          galleryId: pendingGalleryId,
        });
      });
      pendingPurgeGalleryIdsRef.current.clear();
    }
  }, []);

  const queryClient = useQueryClient();
  const { removeItemsForGallery } = useUploadQueue();

  const purgeGalleryIdentity = useCallback(
    (identity, activeGalleryIds = []) => {
      const activeIds = new Set(activeGalleryIds.filter(Boolean));
      const purgeGalleryIds = new Set(
        [identity.routeId, identity.canonicalId].filter(
          (candidateId) => candidateId && !activeIds.has(candidateId)
        )
      );

      if (purgeGalleryIds.size > 0) {
        queryClient.removeQueries({
          predicate: (query) => {
            const last = query.queryKey[query.queryKey.length - 1];
            return typeof last === 'string' && purgeGalleryIds.has(last);
          },
        });

        purgeGalleryIds.forEach((queueGalleryId) => {
          removeItemsForGallery(queueGalleryId).catch((error) => {
            console.warn('Failed to purge upload queue for previous gallery:', error);
          });
        });

        purgeGalleryIds.forEach((purgeGalleryId) => {
          pendingPurgeGalleryIdsRef.current.add(purgeGalleryId);
        });
      }

      flushPendingPurges(activeGalleryIds);
    },
    [flushPendingPurges, queryClient, removeItemsForGallery]
  );

  useEffect(() => {
    const previousIdentity = currentGalleryIdentityRef.current;
    if (previousIdentity.routeId && previousIdentity.routeId !== routeGalleryId) {
      purgeGalleryIdentity(previousIdentity, [routeGalleryId]);
    }
    currentGalleryIdentityRef.current = {
      routeId: routeGalleryId,
      canonicalId: routeGalleryId,
    };
    flushPendingPurges([routeGalleryId]);
  }, [routeGalleryId, flushPendingPurges, purgeGalleryIdentity]);

  useEffect(() => {
    const serviceWorker = navigator.serviceWorker;
    if (!serviceWorker?.addEventListener) {
      return undefined;
    }
    const handleControllerChange = () => {
      const currentIdentity = currentGalleryIdentityRef.current;
      flushPendingPurges([currentIdentity.routeId, currentIdentity.canonicalId]);
    };
    serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      serviceWorker.removeEventListener?.('controllerchange', handleControllerChange);
    };
  }, [flushPendingPurges]);

  const loadPublicConfig = useCallback(async () => {
    const requestId = ++configRequestRef.current;
    const trimmed = routeGalleryId;
    if (!trimmed) {
      setError('not-found');
      setLoading(false);
      setPublicConfig(null);
      return;
    }
    configRouteGalleryIdRef.current = trimmed;
    setStoredGalleryId(trimmed);
    setLoading(true);
    setError(null);
    setPublicConfig(null);
    try {
      const path = `/galleries/${encodeURIComponent(trimmed)}/public-config`;
      const { data } = bootstrapPreviewToken
        ? await api.get(path, {
            headers: { 'x-preview-token': bootstrapPreviewToken },
          })
        : await api.get(path);
      const canonicalId =
        typeof data.galleryId === 'string' && data.galleryId.trim()
          ? data.galleryId.trim()
          : trimmed;
      if (currentGalleryIdentityRef.current.routeId !== trimmed) {
        const activeIdentity = currentGalleryIdentityRef.current;
        purgeGalleryIdentity(
          { routeId: trimmed, canonicalId },
          [activeIdentity.routeId, activeIdentity.canonicalId]
        );
        return;
      }
      if (configRequestRef.current !== requestId) return;
      currentGalleryIdentityRef.current = {
        routeId: trimmed,
        canonicalId,
      };
      flushPendingPurges([trimmed, canonicalId]);
      setStoredGalleryId(canonicalId);
      /** @type {PublicConfig} */
      const config = {
        galleryId: canonicalId,
        name: typeof data.name === 'string' ? data.name : '',
        status: typeof data.status === 'string' ? data.status : 'unknown',
        goLiveAt: typeof data.goLiveAt === 'string' ? data.goLiveAt : null,
        previewAuthorized: data.previewAuthorized === true,
        theme: data.theme && typeof data.theme === 'object' ? data.theme : null,
        tabs: data.tabs && typeof data.tabs === 'object' ? data.tabs : null,
        logo: data.logo && typeof data.logo === 'object' ? data.logo : null,
        deployedAt: data.deployedAt ?? null,
      };
      setPublicConfig(config);
      applyPublicConfigToStorageAndUi(config);
      setError(null);
    } catch (err) {
      if (currentGalleryIdentityRef.current.routeId !== trimmed || configRequestRef.current !== requestId) {
        return;
      }
      const status = err.response?.status;
      if (status === 404 || status === 400) {
        setError('not-found');
      } else {
        setError('network');
      }
      setPublicConfig(null);
    } finally {
      if (currentGalleryIdentityRef.current.routeId === trimmed && configRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    routeGalleryId,
    bootstrapPreviewToken,
    flushPendingPurges,
    purgeGalleryIdentity,
  ]);

  useEffect(() => {
    loadPublicConfig();
    return () => { configRequestRef.current += 1; };
  }, [loadPublicConfig]);

  const activePublicConfig =
    configRouteGalleryIdRef.current === routeGalleryId ? publicConfig : null;
  const isRouteLoading =
    loading || (!!routeGalleryId && configRouteGalleryIdRef.current !== routeGalleryId);
  const logoUrl = useMemo(
    () => normalizeLogoForSpinner(activePublicConfig),
    [activePublicConfig]
  );

  const verifiedPreviewToken =
    activePublicConfig?.previewAuthorized === true ? bootstrapPreviewToken : '';
  const value = useMemo(
    () => ({
      galleryId: activePublicConfig?.galleryId || routeGalleryId,
      publicConfig: activePublicConfig,
      loading: isRouteLoading,
      error,
      logoUrl,
      previewToken: verifiedPreviewToken,
      // True when a preview token was offered for this gallery (URL bootstrap or restored after an
      // iframe reload), whether or not the server authorized it. An unauthorized offer must show
      // "preview unavailable", never fall back to the couple's own guest session.
      previewRequested: bootstrapPreviewToken !== '',
      // True only for a portal preview session the server has authorized. Preview cannot act as a
      // guest (#370), so check-in, guestbook and Q&A controls are disabled while this is true;
      // owner uploads stay enabled.
      isPreview: verifiedPreviewToken !== '',
      refetchPublicConfig: loadPublicConfig,
    }),
    [
      activePublicConfig,
      verifiedPreviewToken,
      bootstrapPreviewToken,
      isRouteLoading,
      error,
      logoUrl,
      loadPublicConfig,
      routeGalleryId,
    ]
  );

  return (
    <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>
  );
}

export function useGallery() {
  const ctx = useContext(GalleryContext);
  if (!ctx) {
    throw new Error('useGallery must be used within GalleryProvider');
  }
  return ctx;
}
